import React, { Component } from "react";
import { Input, Modal } from "reactstrap";
import {
  getMasterCatalog,
  getMasterCatalogItem,
  setCatalogGlobalAvailability,
  getBranchList,
} from "../../Endpoint/Admin/index";
import { toast } from "react-toastify";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import {
  C, Panel, StatCard, StatRow, SearchPill, Table, Chip, Btn, IconBtn, TabPills, ConfirmDialog,
} from "../../components/Console/ConsoleUI";
import { canWrite } from "../../services/PermissionService";

/**
 * Master catalog — the hotel's single list of everything it sells, restyled
 * onto the console shell.
 *
 * The screen's whole point is that it is NOT per-branch: discontinuing here
 * withdraws an item from every branch at once. That is stated once, plainly,
 * above the table rather than buried in a tooltip.
 *
 * A re-skin only: the same getMasterCatalog(null, true) load, the same
 * client-side pagination over it, the same per-item detail call and the same
 * availability writes.
 */

// "finalAmount" -> "Final amount"
const humanizeField = (field) =>
  String(field)
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim()
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());

const money = (value) => {
  const n = Number(value);
  if (!isFinite(n)) return null;
  return `$${n % 1 === 0 ? n : n.toFixed(2)}`;
};

const PAGE_SIZES = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

// Fields the detail panel never shows (ids are noise for a human reader).
const DETAIL_HIDDEN_FIELDS = ["_id", "id", "__v"];
const DETAIL_DATE_FIELDS = ["createdAt", "updatedAt"];

const isPlainObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

const safeJson = (value) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch (e) {
    return String(value);
  }
};

const readableDate = (value) => {
  const d = new Date(value);
  if (!value || isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
};

/**
 * Windowed page numbers — at most 7 entries, with "…" standing in for the
 * pages that are skipped so a 40-page catalog does not print 40 links.
 */
const pageWindow = (current, total) => {
  if (total <= 7) {
    const all = [];
    for (let i = 1; i <= total; i++) all.push(i);
    return all;
  }
  let start;
  let end;
  if (current <= 4) {
    start = 2;
    end = 5;
  } else if (current >= total - 3) {
    start = total - 4;
    end = total - 1;
  } else {
    start = current - 1;
    end = current + 1;
  }
  const pages = [1];
  if (start > 2) pages.push("…");
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < total - 1) pages.push("…");
  pages.push(total);
  return pages;
};

/** Scoped CSS — inline styles cannot reach a native checkbox or the modal shell. */
const CSS = `
.mc-modal .modal-content{border:0;border-radius:22px;background:#fdf9f3;
  box-shadow:0 24px 60px rgba(0,0,0,.35);overflow:hidden}
.mc-form .form-control,.mc-form .custom-select{height:38px;padding:0 12px;background:#fff;
  border:1px solid ${C.border};border-radius:10px;font-size:14px;color:${C.ink};box-shadow:none}
.mc-form .form-control:focus{border-color:${C.sand};box-shadow:none}
.mc-check{accent-color:${C.brown};width:16px;height:16px;cursor:pointer}
.mc-json{margin:0;padding:10px 12px;border-radius:10px;background:${C.surfaceAlt};
  color:${C.brownDeep};font-size:12px;max-height:180px;overflow:auto}
`;

class MasterCatalog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoaded: false,
      groups: [],
      branchMap: {}, // { branchId: "Branch name" }
      branchCount: 0,
      activeType: "",
      search: "",
      statusFilter: "all", // all | active | inactive
      selected: {}, // { itemId: true } — scoped to the active type, kept across pages
      saving: false,
      // pagination (client-side — the API returns the whole catalog in one go)
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      // item detail panel
      detailOpen: false,
      detailLoading: false,
      detail: null,
      detailItemId: null,
      // confirmation modal
      confirmOpen: false,
      confirmIds: [],
      confirmNextActive: false,
      confirmLabel: "",
    };
  }

  componentDidMount() {
    this.loadBranches();
    this.loadCatalog();
  }

  // ---------------------------------------------------------------- loading

  loadBranches = () => {
    getBranchList()
      .then((result) => {
        if (result && result.status === 200 && Array.isArray(result.data)) {
          const branchMap = {};
          result.data.forEach((b) => {
            branchMap[String(b._id)] = b.name || b.code || String(b._id);
          });
          this.setState({ branchMap, branchCount: result.data.length });
        }
      })
      .catch(() => {
        /* branch names are cosmetic — ids are shown as a fallback */
      });
  };

  // includeInactive MUST be true, otherwise discontinued items never appear and
  // could never be reinstated from this screen.
  loadCatalog = () => {
    getMasterCatalog(null, true)
      .then((result) => {
        if (result && result.status === 200 && result.data) {
          const groups = result.data.groups || [];
          const activeType =
            this.state.activeType &&
            groups.some((g) => g.key === this.state.activeType)
              ? this.state.activeType
              : (groups[0] || {}).key || "";
          this.setState({ isLoaded: true, groups, activeType });
        } else {
          this.setState({ isLoaded: true });
          toast.error((result && result.message) || "Failed to load master catalog");
        }
      })
      .catch(() => {
        this.setState({ isLoaded: true });
        toast.error("Error loading master catalog");
      });
  };

  // ---------------------------------------------------------------- helpers

  activeGroup = () =>
    this.state.groups.find((g) => g.key === this.state.activeType) || {
      key: "",
      label: "",
      adminRoute: "",
      priceFields: [],
      items: [],
    };

  // Items of the active type after search + status filtering.
  visibleItems = () => {
    const { search, statusFilter } = this.state;
    const needle = search.trim().toLowerCase();
    return (this.activeGroup().items || []).filter((it) => {
      if (needle && !String(it.name || "").toLowerCase().includes(needle)) return false;
      if (statusFilter === "active" && it.isActive === false) return false;
      if (statusFilter === "inactive" && it.isActive !== false) return false;
      return true;
    });
  };

  // How many pages the current filter needs (always at least one).
  pageCount = (visibleCount) =>
    Math.max(1, Math.ceil(visibleCount / (this.state.pageSize || DEFAULT_PAGE_SIZE)));

  // The page actually being shown — clamped, so deleting/filtering rows while
  // sitting on the last page cannot strand the user on an empty page.
  currentPage = (visibleCount) => Math.min(Math.max(1, this.state.page), this.pageCount(visibleCount));

  // The slice of `visible` rendered right now.
  pageItems = (visible) => {
    const size = this.state.pageSize || DEFAULT_PAGE_SIZE;
    const start = (this.currentPage(visible.length) - 1) * size;
    return visible.slice(start, start + size);
  };

  branchName = (id) => this.state.branchMap[String(id)] || String(id);

  branchNames = (ids) => (ids || []).map(this.branchName).join(", ");

  /**
   * Translate the raw branch arrays into plain English.
   *
   *   no branchIds  + no excludedBranchIds -> "All branches"
   *   branchIds non-empty                  -> "Only: <names>"
   *   excludedBranchIds non-empty          -> "All except: <names>"
   *
   * An item can carry both (an explicit include list AND exclusions); exclusion
   * beats inclusion server-side, so both clauses are shown when both are set.
   */
  availabilityText = (item) => {
    const included = item.branchIds || [];
    const excluded = item.excludedBranchIds || [];
    if (!included.length && !excluded.length) return "All branches";
    const parts = [];
    if (included.length) parts.push(`Only: ${this.branchNames(included)}`);
    if (excluded.length) parts.push(`All except: ${this.branchNames(excluded)}`);
    return parts.join(" — ");
  };

  /** Everywhere / restricted, for the chip tone beside the sentence. */
  isEverywhere = (item) =>
    !(item.branchIds || []).length && !(item.excludedBranchIds || []).length;

  totals = () => {
    let total = 0;
    let active = 0;
    this.state.groups.forEach((g) => {
      (g.items || []).forEach((it) => {
        total += 1;
        if (it.isActive !== false) active += 1;
      });
    });
    return { total, active, inactive: total - active };
  };

  selectedIds = () => Object.keys(this.state.selected).filter((id) => this.state.selected[id]);

  // ---------------------------------------------------------------- handlers

  switchType = (key) => {
    if (key === this.state.activeType) return;
    // Selection is per-type — a bulk action always targets one catalog type.
    this.setState({ activeType: key, selected: {}, search: "", page: 1 });
  };

  handleSearch = (value) => this.setState({ search: value, selected: {}, page: 1 });

  handleStatusFilter = (e) =>
    this.setState({ statusFilter: e.target.value, selected: {}, page: 1 });

  handlePageSize = (e) =>
    this.setState({ pageSize: Number(e.target.value) || DEFAULT_PAGE_SIZE, page: 1 });

  goToPage = (page, visibleCount) => {
    const target = Math.min(Math.max(1, page), this.pageCount(visibleCount));
    if (target !== this.currentPage(visibleCount)) this.setState({ page: target });
  };

  toggleRow = (id) => {
    const selected = { ...this.state.selected };
    if (selected[id]) delete selected[id];
    else selected[id] = true;
    this.setState({ selected });
  };

  // Header checkbox acts on THIS PAGE only; anything picked on other pages is
  // left alone so a selection can be built up across several pages.
  toggleSelectAll = () => {
    const pageItems = this.pageItems(this.visibleItems());
    if (!pageItems.length) return;
    const allSelected = pageItems.every((it) => this.state.selected[it._id]);
    const selected = { ...this.state.selected };
    pageItems.forEach((it) => {
      if (allSelected) delete selected[it._id];
      else selected[it._id] = true;
    });
    this.setState({ selected });
  };

  clearSelection = () => this.setState({ selected: {} });

  // ---------------------------------------------------------------- detail panel

  openDetail = (item) => {
    const type = this.state.activeType;
    if (!type || !item || !item._id) return;
    this.setState({
      detailOpen: true,
      detailLoading: true,
      detail: null,
      detailItemId: String(item._id),
    });
    getMasterCatalogItem(type, item._id)
      .then((result) => {
        if (result && result.status === 200 && result.data) {
          this.setState({ detailLoading: false, detail: result.data });
        } else {
          this.setState({ detailOpen: false, detailLoading: false, detail: null });
          toast.error((result && result.message) || "Failed to load the item");
        }
      })
      .catch(() => {
        this.setState({ detailOpen: false, detailLoading: false, detail: null });
        toast.error("Error loading the item");
      });
  };

  closeDetail = () => {
    if (this.state.saving) return;
    this.setState({ detailOpen: false, detailLoading: false, detail: null, detailItemId: null });
  };

  // Reinstating is non-destructive, so it applies straight away; discontinuing
  // withdraws an item from every branch, so it always asks first.
  askDiscontinue = (ids, label) => {
    if (!ids.length) return;
    this.setState({
      confirmOpen: true,
      confirmIds: ids,
      confirmNextActive: false,
      confirmLabel: label,
    });
  };

  closeConfirm = () => {
    if (this.state.saving) return;
    this.setState({ confirmOpen: false, confirmIds: [], confirmLabel: "" });
  };

  confirmAction = () => {
    this.applyAvailability(this.state.confirmIds, this.state.confirmNextActive);
  };

  applyAvailability = (ids, isActive) => {
    if (!ids.length) return;
    const type = this.state.activeType;
    this.setState({ saving: true });
    setCatalogGlobalAvailability(type, ids, isActive)
      .then((result) => {
        if (result && result.status === 200) {
          toast.success(result.message || "Catalog updated");
          this.setState(
            {
              saving: false,
              confirmOpen: false,
              confirmIds: [],
              confirmLabel: "",
              selected: {},
              // the detail panel's copy of the record is now stale
              detailOpen: false,
              detailLoading: false,
              detail: null,
              detailItemId: null,
            },
            this.loadCatalog
          );
        } else {
          this.setState({ saving: false });
          toast.error((result && result.message) || "Failed to update availability");
        }
      })
      .catch(() => {
        this.setState({ saving: false });
        toast.error("Error updating availability");
      });
  };

  // ---------------------------------------------------------------- renderers

  renderPrice = (item, priceFields) => {
    if (!priceFields || !priceFields.length) return <span style={{ color: C.taupeLight }}>—</span>;
    const price = item.price || {};
    const shown = priceFields.filter((f) => price[f] !== undefined && price[f] !== null);
    if (!shown.length) return <span style={{ color: C.taupeLight }}>—</span>;
    // Prefer the field that represents what the guest actually pays.
    const primary =
      ["finalAmount", "total", "amount", "basePrice", "fixedAmount"].find((f) => shown.includes(f)) ||
      shown[0];
    const rest = shown.filter((f) => f !== primary);
    return (
      <div>
        <span style={{ fontSize: 15, fontWeight: 500, color: C.ink }}>{money(price[primary])}</span>
        {rest.length > 0 && (
          <div style={{ marginTop: 2, fontSize: 11, color: C.taupeLight }}>
            {rest.map((f) => `${humanizeField(f)} ${money(price[f])}`).join(" · ")}
          </div>
        )}
      </div>
    );
  };

  // "Menu items" -> "Add Menu item". Items are created on their own admin page;
  // the master catalog is a cross-type VIEW, so it links there rather than
  // pretending it can create.
  addNewLabel = (group) =>
    group && group.label ? `Add ${String(group.label).replace(/s$/i, "")}` : "Add new";

  renderAddNewButton = (group, kind = "primary") => {
    if (!group || !group.adminRoute) return null;
    return (
      <Btn kind={kind} icon="add" onClick={() => this.props.history.push(group.adminRoute)}>
        {this.addNewLabel(group)}
      </Btn>
    );
  };

  columns(priceFields, pageItems) {
    const write = canWrite();
    const allChecked = pageItems.length > 0 && pageItems.every((it) => this.state.selected[it._id]);
    const someChecked = pageItems.some((it) => this.state.selected[it._id]);

    return [
      {
        key: "pick",
        width: 40,
        label: (
          <input
            type="checkbox"
            className="mc-check"
            aria-label="Select all on this page"
            checked={allChecked}
            disabled={pageItems.length === 0}
            ref={(el) => {
              if (el) el.indeterminate = someChecked && !allChecked;
            }}
            onChange={this.toggleSelectAll}
          />
        ),
        render: (it) => (
          <div onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              className="mc-check"
              aria-label={`Select ${it.name}`}
              checked={!!this.state.selected[it._id]}
              onChange={() => this.toggleRow(it._id)}
            />
          </div>
        ),
      },
      {
        key: "name",
        label: "Item",
        render: (it) => (
          <div style={{ fontSize: 15, color: C.ink }}>{it.name || "Untitled"}</div>
        ),
      },
      {
        key: "price",
        label: "Price",
        width: 150,
        render: (it) => this.renderPrice(it, priceFields),
      },
      {
        key: "availability",
        label: "Sold at",
        render: (it) => (
          <span
            title={this.availabilityText(it)}
            style={{
              fontSize: 13,
              color: this.isEverywhere(it) ? C.taupe : C.brownDeep,
              display: "inline-block", maxWidth: 300,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}
          >
            {this.availabilityText(it)}
          </span>
        ),
      },
      {
        key: "status",
        label: "Status",
        width: 130,
        render: (it) =>
          it.isActive !== false ? (
            <Chip tone="good">Active</Chip>
          ) : (
            <Chip tone="bad">Discontinued</Chip>
          ),
      },
      {
        key: "actions",
        label: "",
        width: write ? 110 : 60,
        align: "right",
        render: (it) => (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}
          >
            <IconBtn icon="visibility" title={`View ${it.name}`} onClick={() => this.openDetail(it)} />
            {/* Availability is a write; a view-only account still sees the state. */}
            {write &&
              (it.isActive !== false ? (
                <IconBtn
                  icon="block"
                  tone="bad"
                  title={`Discontinue ${it.name} everywhere`}
                  disabled={this.state.saving}
                  onClick={() => this.askDiscontinue([it._id], it.name)}
                />
              ) : (
                <IconBtn
                  icon="restart_alt"
                  title={`Reinstate ${it.name} everywhere`}
                  disabled={this.state.saving}
                  onClick={() => this.applyAvailability([it._id], true)}
                />
              ))}
          </div>
        ),
      },
    ];
  }

  /**
   * One value of the detail panel's `fields` object, formatted for a human.
   * Anything that is not a primitive falls back to compact JSON rather than
   * risking a crash on an unexpected shape.
   */
  renderDetailValue = (key, value) => {
    if (value === null || value === undefined || value === "") {
      return <span style={{ color: C.taupeLight }}>—</span>;
    }
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (DETAIL_DATE_FIELDS.includes(key)) return readableDate(value);
    if (Array.isArray(value)) {
      if (!value.length) return <span style={{ color: C.taupeLight }}>—</span>;
      const allPrimitive = value.every((v) => v === null || typeof v !== "object");
      if (allPrimitive) {
        return value.map((v) => (v === null || v === undefined ? "—" : String(v))).join(", ");
      }
      return <pre className="mc-json">{safeJson(value)}</pre>;
    }
    if (isPlainObject(value)) {
      return <pre className="mc-json">{safeJson(value)}</pre>;
    }
    return String(value);
  };

  renderDetailModal = () => {
    const s = this.state;
    const d = s.detail;
    const isActive = d ? d.isActive !== false : true;
    const fields = d && isPlainObject(d.fields) ? d.fields : {};
    const keys = Object.keys(fields).filter((k) => !DETAIL_HIDDEN_FIELDS.includes(k));

    return (
      <Modal
        isOpen={s.detailOpen}
        toggle={this.closeDetail}
        size="lg"
        centered
        className="con-modal mc-modal"
      >
        <div style={{ padding: 30 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
            <span
              className="ms"
              style={{ fontSize: 24, color: C.brown, background: C.iconTint, padding: 12, borderRadius: 15 }}
            >
              inventory_2
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 21, fontWeight: 500, color: C.ink }}>
                {s.detailLoading || !d ? "Item detail" : d.name || "Untitled"}
              </div>
              <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {!s.detailLoading && d && (
                  <React.Fragment>
                    {isActive ? <Chip tone="good">Active</Chip> : <Chip tone="bad">Discontinued</Chip>}
                    <span style={{ fontSize: 13, color: C.taupe }}>{d.label || "Item"} · full record</span>
                  </React.Fragment>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={this.closeDetail}
              aria-label="Close"
              style={{ border: 0, background: "transparent", color: C.taupeLight, cursor: "pointer", padding: 4, lineHeight: 1 }}
            >
              <span className="ms" style={{ fontSize: 22 }}>close</span>
            </button>
          </div>

          {s.detailLoading || !d ? (
            <div style={{ padding: "48px 0", textAlign: "center", color: C.taupeLight }}>
              Loading item detail…
            </div>
          ) : (
            <React.Fragment>
              <div
                style={{
                  marginTop: 20, padding: "14px 16px", borderRadius: 14,
                  background: C.iconTint, display: "flex", alignItems: "center", gap: 10,
                }}
              >
                <span className="ms" style={{ fontSize: 20, color: C.brown }}>storefront</span>
                <div style={{ fontSize: 14, color: C.brownDeep }}>
                  <span style={{ color: C.taupe }}>Sold at: </span>
                  {(d.availability && d.availability.summary) || "All branches"}
                </div>
              </div>

              <div style={{ marginTop: 18, maxHeight: 340, overflowY: "auto" }}>
                {keys.length === 0 ? (
                  <div style={{ fontSize: 14, color: C.taupeLight }}>
                    No further detail on this record.
                  </div>
                ) : (
                  keys.map((k, i) => (
                    <div
                      key={k}
                      style={{
                        display: "flex", alignItems: "flex-start", gap: 16, padding: "10px 0",
                        borderBottom: i === keys.length - 1 ? "none" : `1px solid ${C.tint}`,
                      }}
                    >
                      <div style={{ flex: "0 0 34%", fontSize: 13, color: C.taupe }}>
                        {humanizeField(k)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0, fontSize: 14, color: C.ink, wordBreak: "break-word" }}>
                        {this.renderDetailValue(k, fields[k])}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div
                style={{
                  marginTop: 24, display: "flex", alignItems: "center",
                  justifyContent: "space-between", gap: 12, flexWrap: "wrap",
                }}
              >
                <div>
                  {d.adminRoute && (
                    <Btn
                      kind="quiet"
                      icon="edit"
                      onClick={() => this.props.history.push(d.adminRoute)}
                    >
                      Edit in {d.label || "admin"}
                    </Btn>
                  )}
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  {canWrite() &&
                    (isActive ? (
                      <Btn
                        kind="danger"
                        icon="block"
                        disabled={s.saving}
                        onClick={() =>
                          this.askDiscontinue(
                            [String(fields._id || s.detailItemId || "")].filter(Boolean),
                            d.name
                          )
                        }
                      >
                        Discontinue everywhere
                      </Btn>
                    ) : (
                      <Btn
                        icon="restart_alt"
                        disabled={s.saving}
                        onClick={() =>
                          this.applyAvailability(
                            [String(fields._id || s.detailItemId || "")].filter(Boolean),
                            true
                          )
                        }
                      >
                        Reinstate everywhere
                      </Btn>
                    ))}
                  <Btn kind="quiet" onClick={this.closeDetail} disabled={s.saving}>Close</Btn>
                </div>
              </div>
            </React.Fragment>
          )}
        </div>
      </Modal>
    );
  };

  renderPager = (visibleCount) => {
    const size = this.state.pageSize || DEFAULT_PAGE_SIZE;
    const pages = this.pageCount(visibleCount);
    const current = this.currentPage(visibleCount);
    const from = visibleCount === 0 ? 0 : (current - 1) * size + 1;
    const to = Math.min(current * size, visibleCount);

    const box = (extra) => ({
      minWidth: 34, height: 34, padding: "0 8px", borderRadius: 10,
      display: "grid", placeItems: "center", fontSize: 14,
      border: `1px solid ${C.border}`, background: "#fff", color: C.slate, ...extra,
    });

    return (
      <div
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 12, padding: "14px 22px", borderTop: `1px solid ${C.panel}`, flexWrap: "wrap",
        }}
      >
        <div style={{ fontSize: 14, color: C.taupeLight }}>
          {visibleCount === 0 ? "Showing 0 of 0" : `Showing ${from}–${to} of ${visibleCount}`}
        </div>

        {pages > 1 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              onClick={() => this.goToPage(current - 1, visibleCount)}
              style={box({
                color: current > 1 ? C.slate : "#bfae99",
                cursor: current > 1 ? "pointer" : "default",
              })}
            >
              <span className="ms" style={{ fontSize: 19 }}>chevron_left</span>
            </span>
            {pageWindow(current, pages).map((p, idx) =>
              p === "…" ? (
                <span key={`gap-${idx}`} style={box({ border: "none", color: C.taupeLight })}>…</span>
              ) : (
                <span
                  key={p}
                  onClick={() => this.goToPage(p, visibleCount)}
                  style={box(
                    p === current
                      ? { background: C.brown, color: "#fff", border: "none", cursor: "pointer" }
                      : { cursor: "pointer" }
                  )}
                >
                  {p}
                </span>
              )
            )}
            <span
              onClick={() => this.goToPage(current + 1, visibleCount)}
              style={box({
                color: current < pages ? C.slate : "#bfae99",
                cursor: current < pages ? "pointer" : "default",
              })}
            >
              <span className="ms" style={{ fontSize: 19 }}>chevron_right</span>
            </span>
          </div>
        )}
      </div>
    );
  };

  render() {
    const s = this.state;
    const group = this.activeGroup();
    const priceFields = group.priceFields || [];
    const visible = this.visibleItems();
    const pageItems = this.pageItems(visible);
    const selectedIds = this.selectedIds();
    const stats = this.totals();

    return (
      <ConsolePage
        title="Master catalog"
        subtitle={
          s.isLoaded
            ? `Everything the hotel sells · ${stats.total} item${stats.total === 1 ? "" : "s"}`
            : "Loading the catalog…"
        }
        actions={
          <React.Fragment>
            <SearchPill
              value={s.search}
              onChange={this.handleSearch}
              placeholder={`Search ${(group.label || "items").toLowerCase()}`}
              width={230}
            />
            <Btn kind="quiet" icon="refresh" onClick={this.loadCatalog} disabled={s.saving}>
              Refresh
            </Btn>
            {this.renderAddNewButton(group)}
          </React.Fragment>
        }
      >
        <style dangerouslySetInnerHTML={{ __html: CSS }} />

        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <StatRow>
            <StatCard icon="inventory_2" value={stats.total} label="Items in the catalog" />
            <StatCard icon="check_circle" value={stats.active} label="On sale" />
            <StatCard icon="block" value={stats.inactive} label="Discontinued" />
            <StatCard icon="apartment" value={s.branchCount} label="Branches served" />
          </StatRow>

          <Panel>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              <span
                className="ms"
                style={{ fontSize: 20, color: C.brown, background: C.iconTint, padding: 9, borderRadius: 12 }}
              >
                info
              </span>
              <div style={{ fontSize: 14, color: C.slate, lineHeight: 1.6 }}>
                This is the hotel's <strong>one master catalog</strong>. Discontinuing an item here
                withdraws it from <strong>every branch at once</strong> — it is not a per-branch
                setting, and nothing is deleted (order history is kept). To choose what a{" "}
                <em>single</em> branch carries, use the catalog pick-list on the{" "}
                <button
                  type="button"
                  onClick={() => this.props.history.push("/branches")}
                  style={{
                    border: 0, background: "transparent", padding: 0, color: C.brown,
                    fontFamily: "inherit", fontSize: 14, cursor: "pointer", textDecoration: "underline",
                  }}
                >
                  Branches
                </button>{" "}
                page.
              </div>
            </div>
          </Panel>

          <Panel style={{ padding: 0 }}>
            <TabPills
              tabs={s.groups.map((g) => ({
                value: g.key,
                label: `${g.label || g.key} · ${(g.items || []).length}`,
              }))}
              value={s.activeType}
              onChange={this.switchType}
              right={
                <div className="mc-form" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Input
                    type="select"
                    aria-label="Status"
                    value={s.statusFilter}
                    onChange={this.handleStatusFilter}
                    style={{ width: 170 }}
                  >
                    <option value="all">All statuses</option>
                    <option value="active">Active only</option>
                    <option value="inactive">Discontinued only</option>
                  </Input>
                  <Input
                    type="select"
                    aria-label="Rows per page"
                    value={s.pageSize}
                    onChange={this.handlePageSize}
                    style={{ width: 130 }}
                  >
                    {PAGE_SIZES.map((n) => (
                      <option key={n} value={n}>{n} per page</option>
                    ))}
                  </Input>
                </div>
              }
            />

            {/* Bulk action bar — only while rows are selected */}
            {selectedIds.length > 0 && canWrite() && (
              <div
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  gap: 12, flexWrap: "wrap", padding: "14px 22px",
                  background: C.surfaceAlt, borderBottom: `1px solid ${C.panel}`,
                }}
              >
                <div style={{ fontSize: 14, color: C.brownDeep }}>
                  <strong>{selectedIds.length}</strong> item{selectedIds.length === 1 ? "" : "s"}{" "}
                  selected — actions apply to <strong>every branch</strong>.
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <Btn
                    kind="danger"
                    icon="block"
                    disabled={s.saving}
                    style={{ height: 36, fontSize: 13 }}
                    onClick={() => this.askDiscontinue(selectedIds, `${selectedIds.length} item(s)`)}
                  >
                    Discontinue everywhere
                  </Btn>
                  <Btn
                    icon="restart_alt"
                    disabled={s.saving}
                    style={{ height: 36, fontSize: 13 }}
                    onClick={() => this.applyAvailability(selectedIds, true)}
                  >
                    Reinstate everywhere
                  </Btn>
                  <Btn
                    kind="quiet"
                    disabled={s.saving}
                    style={{ height: 36, fontSize: 13 }}
                    onClick={this.clearSelection}
                  >
                    Clear
                  </Btn>
                </div>
              </div>
            )}

            <div style={{ padding: "0 10px" }}>
              {!s.isLoaded ? (
                <div style={{ padding: "48px 0", textAlign: "center", color: C.taupeLight }}>
                  Loading master catalog…
                </div>
              ) : (
                <Table
                  columns={this.columns(priceFields, pageItems)}
                  rows={pageItems}
                  onRowClick={this.openDetail}
                  empty={
                    (group.items || []).length === 0
                      ? `No ${(group.label || "items").toLowerCase()} in the master catalog yet — items are created on their own admin page`
                      : "No items match the current search or filter"
                  }
                />
              )}
            </div>

            {s.isLoaded && this.renderPager(visible.length)}
          </Panel>

          {s.isLoaded && (group.items || []).length === 0 && group.adminRoute && (
            <div>{this.renderAddNewButton(group, "quiet")}</div>
          )}
        </div>

        {this.renderDetailModal()}

        <ConfirmDialog
          isOpen={s.confirmOpen}
          toggle={this.closeConfirm}
          onConfirm={this.confirmAction}
          icon="block"
          title="Discontinue everywhere?"
          body={`This withdraws ${s.confirmLabel} from every branch at once.`}
          tip="Nothing is deleted and past orders are kept — you can reinstate it later from this page."
          confirmText="Discontinue everywhere"
          loading={s.saving}
        />
      </ConsolePage>
    );
  }
}

export default MasterCatalog;
